import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore, type AuthTokens } from '@/stores/authStore';

/**
 * Axios-based API client with a single interceptor chain:
 *
 *   request  → attach `Authorization: Bearer <access>` when present
 *   response → on 401, try to refresh the token exactly once, then
 *              replay the original request
 *
 * Multiple concurrent requests can race into the 401 branch; we queue
 * them behind a single refresh promise so we never hit /auth/refresh more
 * than once per access-token expiry.
 */

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const { tokens, user } = useAuthStore.getState();
  if (tokens?.accessToken) {
    config.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }
  // Surface the user's actively-selected role (UserMenu role switcher,
  // epic 115). Only sent when it differs from the JWT-baked primary
  // role — keeps the wire clean for single-role users. The backend
  // treats this as a UX signal; permissions are still resolved against
  // the JWT role union, so no auth bypass risk.
  if (user && user.currentRole && user.currentRole !== user.role) {
    config.headers.set('X-Active-Role', user.currentRole);
  }
  return config;
});

// Concurrent-refresh coordination.
let refreshPromise: Promise<AuthTokens> | null = null;

async function refreshTokens(): Promise<AuthTokens> {
  const current = useAuthStore.getState().tokens;
  if (!current) {
    throw new Error('No refresh token available');
  }

  // Use a bare axios call so the retry path doesn't re-enter our
  // interceptor chain and loop on 401.
  const response = await axios.post<AuthTokens>('/api/v1/auth/refresh', {
    refreshToken: current.refreshToken,
  });

  useAuthStore.getState().setTokens(response.data);
  return response.data;
}

/**
 * Coordinated session refresh exposed for the federation session bridge
 * (123.3): a federated remote calls `__KAIROS_SESSION__.refresh()` on a 401,
 * which routes here. Shares the same single-flight `refreshPromise` as the
 * interceptor so a remote's refresh and a host request never double-hit
 * /auth/refresh.
 */
export async function refreshSession(): Promise<void> {
  refreshPromise ??= refreshTokens().finally(() => {
    refreshPromise = null;
  });
  await refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    // Only try to refresh on 401 from a non-auth endpoint, and only once.
    const is401 = error.response?.status === 401;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');
    if (!is401 || !originalRequest || originalRequest._retried || isRefreshCall) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    try {
      refreshPromise ??= refreshTokens().finally(() => {
        refreshPromise = null;
      });
      const fresh = await refreshPromise;
      originalRequest.headers.set('Authorization', `Bearer ${fresh.accessToken}`);
      return apiClient.request(originalRequest);
    } catch (refreshError) {
      // Refresh failed — the session is dead; clear and let the error
      // bubble so the route guard can kick the user back to /login.
      useAuthStore.getState().clear();
      return Promise.reject(refreshError);
    }
  },
);

/**
 * Extract a human-readable message from an unknown thrown value.
 *
 * Backend errors arrive as an axios error whose response body carries a
 * RFC-7807-style `detail` (and sometimes `message`) field; everything else
 * (network failures, thrown strings) falls back to its string form. Use this
 * in `catch (err)` blocks instead of `catch (err: any)` so error handling
 * stays type-safe.
 */
export function errorDetail(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail || data?.message || err.message;
  }
  return String(err);
}
